/**
 * Single source of truth for role-based access, shared by frontend and backend.
 *
 * Two tables, one file:
 *  - API_ACCESS: capability → roles. The backend gates every role-guarded
 *    endpoint with `requireCapability('<capability>')` (server/middleware/auth.ts),
 *    and handler-internal role branches use `roleHasCapability`.
 *  - ROUTE_ACCESS: frontend route → roles. Consumed by `hasRouteAccess`
 *    (src/utils/accessControl.ts), which also powers nav visibility. Rows point
 *    at API_ACCESS entries wherever a page maps 1:1 to a backend surface, so a
 *    page and its API can never disagree about who gets in.
 *
 * There are three ways to change access: edit a role row, delegate one
 * capability to a named user through CAPABILITY_DELEGATIONS, or add a role.
 * Each policy change also updates shared/__tests__/accessPolicy.test.ts so it
 * shows up as a reviewable test diff. Route/endpoint tests derive their role
 * expectations from these tables at runtime and need no edits.
 *
 * Adding a role (e.g. a future content reviewer):
 *  1. Add the id to ROLES in shared/roles.ts.
 *  2. Append it to the rows for the surfaces it may use.
 *  3. Update the mirror test and DEFAULT_REDIRECTS (src/config/roles.ts).
 * Nothing else: route guards, nav, endpoint gates, and their tests all derive.
 *
 * Role charters (ADMIN decided 2026-07 and widened 2026-09; content_reviewer
 * planned, not yet active):
 *  - ADMIN = operations staff. Exactly: leads, class codes (incl. visit
 *    monitoring), enrollments, access codes, the super-admin view of
 *    My Classes, student billing (Payments Due, the class Billing tab,
 *    payment confirm/record/void — D1, 2026-09), and class changes (deciding
 *    teachers' "Can't make this class?" requests, teacher availability —
 *    2026-10). Deliberately NOT attendance
 *    marking or teacher pay (those stay super-admin), user management, help
 *    requests, feature flags, curriculum/content tooling, or cross-student
 *    analytics.
 *  - content_reviewer (future, id not yet added) = practice, lesson prep,
 *    mastery map, curriculum review, adaptive templates — the rows to extend
 *    are worksheets:manage, curriculum:review, adaptiveTemplates:review,
 *    practice:simulateCohorts, and the '/practice' / '/progress' routes.
 */
import {
  hasFeatureAccess,
  type FeatureFlagUser,
} from './featureFlagEvaluation.js';
import type { FeatureFlagId } from './featureFlags.js';
import type { RoleId } from './roles.js';
import { ROLES } from './roles.js';

const { SUPER_ADMIN, ADMIN, TEACHER, USER, AFFILIATE } = ROLES;

/**
 * Every role that belongs to the tutoring product itself. Excludes AFFILIATE by
 * design: an affiliate is an external marketing partner, not a member of a
 * school, so the shared student/staff surfaces (class and practice content)
 * are not theirs to see. A new *internal* role belongs in this list; a new
 * external one does not.
 */
export const INTERNAL_ROLES: readonly RoleId[] = [
  SUPER_ADMIN,
  ADMIN,
  TEACHER,
  USER,
];

const ALL_AUTHENTICATED = INTERNAL_ROLES;

/**
 * Capability → roles. Grouped by domain; one row usually guards a whole
 * feature's endpoints, split by verb only where the audiences differ.
 */
export const API_ACCESS = {
  // Leads CRM (the ops-staff core job)
  'leads:manage': [ADMIN, SUPER_ADMIN],
  'leads:hide': [SUPER_ADMIN],
  'leads:attributionReport': [SUPER_ADMIN], // marketing performance, not ops
  'partners:read': [ADMIN, SUPER_ADMIN], // leads-page filter dropdowns
  'partners:manage': [SUPER_ADMIN],
  // The IP addresses and browsers behind a partner's math checks: evidence if
  // a partner's leads are faked. Never partners, and not ops staff either.
  'partnerEvidence:read': [SUPER_ADMIN],

  // Class codes admin page (incl. waiting-room visit monitoring)
  'classCodes:manage': [ADMIN, SUPER_ADMIN],
  'classLifecycle:manage': [SUPER_ADMIN],
  'classVisits:view': [ADMIN, SUPER_ADMIN],
  'classOptions:read': [ADMIN, SUPER_ADMIN], // dropdown feed for admin pages
  // The zoom-conflicts panel on the class-codes page, and the Zoom account
  // suggester in every admin schedule dialog: both read the fleet's bookings.
  'zoomConflicts:read': [ADMIN, SUPER_ADMIN],
  // The review half of teacher break-glass. Teachers end real meetings with
  // real people in them and no admin approves it, so someone must be able to
  // see afterwards what was closed and by whom — read-only, no credentials, no
  // ability to end anything. ADMIN because ops staff are who is awake when a
  // class-time incident happens; withholding it would make the log unreadable by
  // the people who field the question.
  'zoomActivity:read': [ADMIN, SUPER_ADMIN],
  'securityActivity:read': [SUPER_ADMIN],
  // Stored Zoom Server-to-Server credentials, and creating real meetings in
  // those paid accounts. Narrower than classCodes:manage on purpose: ops staff
  // schedule classes, but holding the machine credentials for a Zoom account is
  // an owner-level act.
  'zoomAccounts:manage': [SUPER_ADMIN],

  // Enrollments + access codes
  'enrollments:manage': [ADMIN, SUPER_ADMIN],
  'accessCodes:manage': [ADMIN, SUPER_ADMIN],

  // Class ops. The session ledger (attendance/marking) and teacher pay
  // statements stay SUPER_ADMIN: a mark decides what a teacher is paid, and
  // "admin-only marking" in the program plan means exactly these two rows
  // (docs/plans/class-ops-program-plan.md).
  'classSessions:manage': [SUPER_ADMIN],
  'teacherStatements:manage': [SUPER_ADMIN],
  'revenue:read': [SUPER_ADMIN],
  // Student billing is ops work (D1, 2026-09): chasing Payments Due and
  // confirming, recording or voiding a parent's payment. Ops staff already did
  // this money through the enrollments page; now that every money act goes
  // through this one capability, this row is what keeps their Confirm button.
  'studentBilling:manage': [ADMIN, SUPER_ADMIN],

  // Class rosters ("My Classes"): teachers read their own; viewAny unlocks the
  // any-teacher picker (server: resolveTargetTeacherId; UI: TeacherClassCodesPage)
  'classes:read': [TEACHER, ADMIN, SUPER_ADMIN],
  'classes:viewAny': [ADMIN, SUPER_ADMIN],
  'classes:zoomManage': [TEACHER],
  // Break-glass: read what is holding a class's Zoom room, and — subject to a
  // server-enforced clearability matrix — close it. TEACHER-only, and
  // deliberately not widened to admins: the failure it exists for happens at
  // class time with students waiting, which is evenings and weekends, and any
  // design routing through an approver fails exactly when it is needed. Admins
  // get the after-the-fact record instead (zoomActivity:read).
  'classes:zoomBreakGlass': [TEACHER],
  // Advisory input to the admin marking flow — classSessions:manage stays the
  // only mark writer. A declaration is what the teacher saw; it never decides
  // money, so it is a teacher row rather than a widening of the row above it.
  'classSessions:declare': [TEACHER],
  // "Can't make this class?": a teacher asks for cover, a move, or flags a
  // date they can't teach (submit, withdraw, acknowledge, read their own, and
  // the students of a class date they teach, for splitting a move). Advisory:
  // a request never changes the class; only an admin approval does.
  'classChanges:request': [TEACHER],
  // Deciding those requests: the Class changes inbox, approve (an ordinary
  // class-code edit run through the admin PATCH pipeline), decline,
  // close-as-handled, the request-scoped Zoom meeting an approval may mint,
  // the availability reads behind the heat-map and relief finder, filling in
  // a teacher's availability for them (the teacher's own record, which they
  // see and can change), and Teacher levels (per level, who to offer first
  // and who never to offer; read and saved here, never shown to teachers).
  // ADMIN because ops staff run the schedule (classCodes:manage); minting a
  // one-off meeting for a pending request is not credential access, so
  // zoomAccounts:manage stays owner-only (docs/access-control.md).
  'classChanges:review': [ADMIN, SUPER_ADMIN],
  // A teacher's own weekly availability grid, dates away and capacity.
  'teacherAvailability:manage': [TEACHER],

  // Teacher surfaces
  // The Students pages (`/teacher/students`, one route family): the class page,
  // the student page and the teacher-scoped submission review, plus the two
  // things those pages write: the TeacherEvent stream and a student's exam
  // dates. The audience of every read and write is the same, so one row gates
  // them all. Every endpoint also checks `teachesClass` (and the student's
  // active membership); super_admins act for a chosen teacher through
  // `?teacherId=`.
  'teacherStudents:read': [TEACHER, SUPER_ADMIN],
  'worksheets:manage': [TEACHER, SUPER_ADMIN], // lesson prep / worksheet builder
  'teacherHomework:manage': [TEACHER],
  // Personal authored-question library. Teachers and super-admins author their
  // own questions; every service call scopes records to the authenticated
  // author + tenant.
  'teacherQuestions:manage': [TEACHER, SUPER_ADMIN],
  // Nonprofit demo view: a flagged sample-teacher account switching its own
  // session into the view of its linked sample student and back — a bounded
  // server-side actor swap (docs/nonprofit-demo-view.md). TEACHER-only on
  // purpose: super_admin uses the separate sandbox:switchView capability.
  'nonprofitDemo:switchView': [TEACHER],
  'sandbox:switchView': [SUPER_ADMIN],

  // Student surfaces
  // Content-serving and answer-grading endpoints. This is intentionally the
  // full set of internal roles rather than "any authenticated user": external
  // roles must never inherit access when they are added to shared/roles.ts.
  'practice:access': INTERNAL_ROLES,
  'homework:complete': [USER],
  'myClassCodes:read': [USER],
  // Shared school planning has its own picker and relationship boundaries.
  'studentPlanning:access': INTERNAL_ROLES,
  'studentPlanning:self': [USER],
  'studentPlanning:teach': [TEACHER, SUPER_ADMIN],
  'studentPlanning:manageStudents': [ADMIN, SUPER_ADMIN],

  // Help requests
  'helpRequests:participate': [TEACHER, SUPER_ADMIN, USER],
  'helpRequests:respond': [TEACHER, SUPER_ADMIN],

  // Content & curriculum
  'questions:flagForReview': [TEACHER, SUPER_ADMIN],
  // A teacher flagging a question from a lesson-prep / homework builder. The
  // cap is the union of the two builder audiences (worksheets:manage is
  // TEACHER+SUPER_ADMIN, teacherHomework:manage is TEACHER-only), so super_admin
  // holds it even though it cannot reach the homework builder — the row it
  // actually uses is the lesson-prep one.
  'questionFeedback:submit': [TEACHER, SUPER_ADMIN],
  // Triaging those flags on /admin/question-feedback. Advisory queue: resolving
  // a flag never mutates the question.
  'questionFeedback:review': [SUPER_ADMIN],
  'curriculum:review': [SUPER_ADMIN], // KG node reads (drift report, audit log)
  'knowledgeGraph:manage': [SUPER_ADMIN], // KG node mutations
  'adaptiveTemplates:review': [SUPER_ADMIN],
  'mathChecks:manage': [SUPER_ADMIN],
  'diagnosticPools:manage': [SUPER_ADMIN],
  'teacherContent:review': [SUPER_ADMIN], // all-worksheets / all-homework admin
  // Cross-author oversight/triage for My Questions. Separate from manage so
  // oversight cannot silently become permission to edit a teacher's library.
  'teacherQuestions:review': [SUPER_ADMIN],

  // Ops & system
  'nonprofitCohorts:manage': [SUPER_ADMIN],
  'featureFlags:manage': [SUPER_ADMIN],
  'productAnalytics:manage': [SUPER_ADMIN],
  'users:manage': [SUPER_ADMIN],
  // Stricter tier inside user management: entitlement/plan changes, account
  // deactivation, student↔teacher link assignment. Stays SUPER_ADMIN even if
  // ops staff are ever granted users:manage.
  'users:manageSensitive': [SUPER_ADMIN],
  // Staff mode on Progress, History and the session review: the page reads a
  // chosen student instead of the viewer's own data, picked on Progress and
  // History. Teachers reach only the session review, from the student page.
  // A UI mode, never a server gate: every read it leads to authorizes itself
  // (`practice:access` plus class co-membership for a teacher).
  'studentPicker:show': [TEACHER, SUPER_ADMIN],
  // Staff practice mode: the level and stream options a staff member may try
  // content as (`/practice/cohort-options`). TEACHER keeps it without the
  // Practice page: Lesson Prep's New-worksheet level picker reads the same
  // options through `useStaffLevelStream`.
  'practice:simulateCohorts': [TEACHER, SUPER_ADMIN],
  // The in-app Join Class menu entry (`/class?inApp=1`). A UI row, never a
  // server gate: `/class` itself stays open to every internal role. Teachers
  // are off it because each My Classes card carries the class's own Join
  // class, and the student join page is not theirs to land on.
  'joinClassMenu:show': [SUPER_ADMIN, ADMIN, USER],

  // Affiliate portal: an external partner reading the click + trial funnel of
  // the partner slugs linked to their own login. Super_admin holds it for
  // oversight, but reaches no numbers on this row alone — the surface resolves
  // its data from Partner.affiliateUserId, and a staff account owns no slugs.
  // The row below is what turns that into a readable portal.
  'affiliateStats:read': [AFFILIATE, SUPER_ADMIN],
  // "View as affiliate": pick whose portal to read instead of resolving self.
  // Read-only projection of the same masked data, never session impersonation.
  // Excludes ADMIN on purpose — partner payouts are an owner-level view.
  'affiliateStats:viewAny': [SUPER_ADMIN],
} as const satisfies Record<string, readonly RoleId[]>;

export type ApiCapability = keyof typeof API_ACCESS;

export const roleHasCapability = (
  role: RoleId | string | null | undefined,
  capability: ApiCapability
): boolean =>
  role != null && (API_ACCESS[capability] as readonly string[]).includes(role);

/**
 * Capability → feature flag. The API_ACCESS row remains the role-level truth;
 * a named user carrying the mapped flag may hold the capability beyond their
 * role. Each flag's role whitelist bounds who may receive the delegation.
 */
export const CAPABILITY_DELEGATIONS = {
  'nonprofitCohorts:manage': 'nonprofit_cohort_management',
} as const satisfies Partial<Record<ApiCapability, FeatureFlagId>>;

export type DelegableCapability = keyof typeof CAPABILITY_DELEGATIONS;

/** Widen the const object's key type for callers holding any capability. */
export const delegatedFlagFor = (
  capability: ApiCapability
): FeatureFlagId | undefined =>
  (CAPABILITY_DELEGATIONS as Partial<Record<ApiCapability, FeatureFlagId>>)[
    capability
  ];

type CapabilityUser =
  | {
      role?: RoleId | string | null;
      featureFlags?: Map<string, boolean> | Readonly<Record<string, boolean>>;
    }
  | null
  | undefined;

/** Role in the policy row OR an eligible per-user delegation. */
export const userHasCapability = (
  user: CapabilityUser,
  capability: ApiCapability
): boolean => {
  if (roleHasCapability(user?.role, capability)) return true;
  const flag = delegatedFlagFor(capability);
  return (
    flag !== undefined &&
    !!user &&
    hasFeatureAccess(user as FeatureFlagUser, flag)
  );
};

/**
 * Frontend route → roles. Rows reference API_ACCESS where the page is the UI
 * for exactly one backend surface; composite/student surfaces inline their set.
 */
export const ROUTE_ACCESS: Record<string, readonly RoleId[]> = {
  '/lesson-prep': API_ACCESS['worksheets:manage'],
  // The student topic browser. TEACHER is off the row: teachers try content
  // in Lesson Prep and see a student's practice on the Students pages.
  '/practice': [SUPER_ADMIN, USER],
  // Student "do now" landing. USER-only: staff have no task inbox (their
  // homework is per-class, not per-self). Practice availability is resolved
  // inside the page so DEFAULT_REDIRECTS[USER] can remain stable.
  '/tasks': [USER],
  // Cold-start placement diagnostic — students only; staff simulate cohorts
  // through the ordinary practice surface and never owe a placement.
  '/diagnostic': [USER],
  '/homework': [USER],
  '/syllabus': API_ACCESS['curriculum:review'],
  '/user-management': API_ACCESS['users:manage'],
  '/admin/users': API_ACCESS['users:manage'],
  '/admin/users/:userId': API_ACCESS['users:manage'],
  '/help-requests': API_ACCESS['helpRequests:respond'],
  '/class': ALL_AUTHENTICATED,
  '/admin/feature-flags': API_ACCESS['featureFlags:manage'],
  '/admin/product-analytics': API_ACCESS['productAnalytics:manage'],
  '/admin/leads': API_ACCESS['leads:manage'],
  '/admin/class-codes': API_ACCESS['classCodes:manage'],
  '/admin/partners': API_ACCESS['partners:manage'],
  '/admin/zoom-accounts': API_ACCESS['zoomAccounts:manage'],
  '/admin/zoom-activity': API_ACCESS['zoomActivity:read'],
  '/admin/security-activity': API_ACCESS['securityActivity:read'],
  '/admin/access-codes': API_ACCESS['accessCodes:manage'],
  '/admin/nonprofit-cohorts': API_ACCESS['nonprofitCohorts:manage'],
  '/admin/nonprofit-organizations': API_ACCESS['nonprofitCohorts:manage'],
  '/admin/nonprofit-organizations/:organizationId':
    API_ACCESS['nonprofitCohorts:manage'],
  '/admin/enrollments': API_ACCESS['enrollments:manage'],
  // Class-ops admin IA. `/admin/classes/:classCodeId` is a dynamic route, so —
  // like /teacher/students/:studentId — the page gates itself on this literal
  // key. It rides the wider of its two tabs' rows (studentBilling is a
  // superset of classSessions); the page gates its Attendance tab on
  // 'classSessions:manage' itself, so an ADMIN gets Billing alone.
  '/admin/attendance': API_ACCESS['classSessions:manage'],
  '/admin/live': API_ACCESS['classVisits:view'],
  // Teachers' "Can't make this class?" requests, decided here.
  '/admin/class-changes': API_ACCESS['classChanges:review'],
  // Who is free when: the heat-map for placing new classes.
  '/admin/teacher-availability': API_ACCESS['classChanges:review'],
  // Teacher levels: per level, who to offer first and who never to offer.
  // Admin-only; a missing row would admit every internal role, TEACHER too.
  '/admin/teacher-levels': API_ACCESS['classChanges:review'],
  '/admin/classes/:classCodeId': API_ACCESS['studentBilling:manage'],
  // Setting where a teacher's Google Calendar invites go. Rides the schedule
  // capability rather than a new row: a calendar is schedule data, and so is
  // the address its invites go to.
  '/admin/teacher-calendar-feeds': API_ACCESS['classSessions:manage'],
  '/admin/teacher-statements': API_ACCESS['teacherStatements:manage'],
  '/admin/revenue': API_ACCESS['revenue:read'],
  '/admin/adaptive-templates': API_ACCESS['adaptiveTemplates:review'],
  '/admin/math-checks': API_ACCESS['mathChecks:manage'],
  '/admin/diagnostic-pools': API_ACCESS['diagnosticPools:manage'],
  '/admin/question-feedback': API_ACCESS['questionFeedback:review'],
  '/admin/worksheets': API_ACCESS['teacherContent:review'],
  '/admin/homework': API_ACCESS['teacherContent:review'],
  '/admin/homework/:id/review': API_ACCESS['teacherContent:review'],
  '/admin/teacher-questions': API_ACCESS['teacherQuestions:review'],
  // The Students route family: the class page, the student page and the
  // teacher-scoped submission review. The two dynamic routes gate on these
  // literal keys through `accessRoute`.
  '/teacher/students': API_ACCESS['teacherStudents:read'],
  '/teacher/students/:studentId': API_ACCESS['teacherStudents:read'],
  '/teacher/students/:studentId/homework/:homeworkId':
    API_ACCESS['teacherStudents:read'],
  '/teacher/class-codes': API_ACCESS['classes:read'],
  '/teacher/homework': API_ACCESS['teacherHomework:manage'],
  // Save as PDF preview of one homework's questions. Rides the homework row:
  // the page reads the homework through the teacher homework API.
  '/teacher/homework/:id/print': API_ACCESS['teacherHomework:manage'],
  '/teacher/questions': API_ACCESS['teacherQuestions:manage'],
  // The affiliate's only surface: everything an affiliate can reach in the app
  // lives behind this route. Super_admin rides the same row for oversight and
  // gets the affiliate picker (affiliateStats:viewAny) rather than a self-view.
  '/affiliate': API_ACCESS['affiliateStats:read'],
  // TEACHER is off the row: a teacher reads their students on the Students
  // pages, so the Progress tab is not theirs.
  '/progress': [SUPER_ADMIN, ADMIN, USER],
  // Look-back surface: homework receipts + practice-session history. Teachers
  // read a student's history on the student page (`/teacher/students/:id`),
  // which supersedes the 2026-07-27 "no history view for teachers" decision
  // without widening this route: the teacher's history is per student, so
  // their bottom-nav tab stays hidden.
  '/history': [SUPER_ADMIN, USER],
  // Read-only projection of the student submission review, opened from a
  // super-admin's selected-student History. The matching API uses the same
  // oversight capability and the student serializer; no write path is shared.
  '/history/homework/:homeworkId': API_ACCESS['teacherContent:review'],
  '/my-requests': [SUPER_ADMIN, USER],
};

/**
 * Route → delegated capability whose UI it exposes. Every ROUTE_ACCESS row
 * sharing a delegated capability's role row must be listed here; the policy
 * mirror test enforces that completeness invariant.
 */
export const ROUTE_DELEGATIONS: Readonly<Record<string, DelegableCapability>> =
  {
    '/admin/nonprofit-cohorts': 'nonprofitCohorts:manage',
    '/admin/nonprofit-organizations': 'nonprofitCohorts:manage',
    '/admin/nonprofit-organizations/:organizationId': 'nonprofitCohorts:manage',
  };
